/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useState, useLayoutEffect, useRef } from "react";
import { withRouter, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "sensis-material-ui";
import Paper from "@material-ui/core/Paper";
import {
  makeStyles,
  MenuItem,
  OutlinedInput,
  Select,
  useTheme,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { setEventSummary } from "../../redux/Actions";
import MainContent from "../common/MainContent";
import { apiService } from "../../services/apiService";
import getDateRange from "../../utils/dateRange";
import moveClicksLast from "../../utils/moveClicksLast";

const MyAccountDashboard = () => {
  const [customerNumber, setCustomerNumber] = useState(null);
  const [apfId, setApfId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState(1);
  const [selectedDate, setSelectedDate] = useState(getDateRange(1));
  const user = useSelector((state) => state.session.user);
  const eventSummary = useSelector((state) => state.eventSummary);
  const isGlobalLoading = useSelector((state) => state.loader);
  const dispatch = useDispatch();
  const history = useHistory();

  const setCustomerNumberAndAfpId = () => {
    if (
      user &&
      (user.contact.customerNumber !== customerNumber ||
        user.contact.apfId !== apfId)
    ) {
      setCustomerNumber(user.contact.customerNumber);
      setApfId(user.contact.apfId);
    }
  };

  const loadEventSummary = async () => {
    setIsLoading(true);
    if (user) {
      const fetchEventSummary = async () => {
        try {
          const response = await apiService.get("catcher/event-summary", {
            ...selectedDate,
            include_rli: false,
            cid: user.contact.customerNumber,
          });
          if (response.Data?.success === false) {
            dispatch(
              setEventSummary(
                moveClicksLast([
                  {
                    event_group_code: "group-4",
                    event_group_name: "calls",
                    total: 0,
                  },
                  {
                    event_group_code: "group-3",
                    event_group_name: "digital contacts",
                    total: 0,
                  },
                  {
                    event_group_code: "group-1",
                    event_group_name: "views",
                    total: 0,
                  },
                  {
                    event_group_code: "group-2",
                    event_group_name: "clicks",
                    total: 0,
                  },
                ])
              )
            );
          } else {
            dispatch(
              setEventSummary(moveClicksLast(response.Data.event_groups))
            );
          }
        } catch (err) {
          console.error("Failed to fetch businesses:", err);
        } finally {
          setIsLoading(false);
        }
      };
      fetchEventSummary();
    }
  };

  useEffect(() => {
    loadEventSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedDate]);

  useEffect(() => {
    setCustomerNumberAndAfpId();
  });

  const { grey600 } = useTheme().custom.colour;

  const useStyles = makeStyles({
    tableHead: {
      backgroundColor: grey600,
    },
    tableUnread: {
      fontWeight: "bold",
    },
    tableCellBtnColumn: {
      backgroundColor: "#000",
    },
    cardsHeader: {
      textTransform: "Capitalize",
    },
    textWrap: {
      wordBreak: "break-all",
      wordWrap: "break-word",
    },
    dropdownTableRow: {
      "&:hover": {
        backgroundColor: "transparent",
      },
      cursor: "default",
    },
    customInput: {
      "& .MuiSelect-select:focus": {
        backgroundColor: "transparent",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "gray",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "gray",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "gray",
      },
    },
  });

  const classes = useStyles();

  const chartsRef = useRef({}); // 👈 Store chart instances per ID

  const eventSummaryChart = (data) => {
    const chartId = data.event_group_code;

    if (chartsRef.current[chartId]) {
      chartsRef.current[chartId].dispose();
    }

    if (chartsRef.current[chartId]) {
      const existingChart = chartsRef.current[chartId];
      existingChart.data = [data];
      existingChart.invalidateRawData();
      return; // stop here, don’t recreate
    }

    const chart = am4core.create(chartId, am4charts.PieChart);
    chart.innerRadius = am4core.percent(60);

    chart.data = [data];

    const pieSeries = chart.series.push(new am4charts.PieSeries());
    pieSeries.dataFields.value = "total";
    pieSeries.dataFields.category = "group_event_name";
    pieSeries.labels.template.disabled = true;
    pieSeries.ticks.template.disabled = true;
    pieSeries.colors.list = [am4core.color("#ffce00")];

    const label = pieSeries.createChild(am4core.Label);
    label.text = data.total > 0 ? data.total : "No data";
    label.horizontalCenter = "middle";
    label.verticalCenter = "middle";
    label.fontSize = 30;
    chart.logo.disabled = true;

    chartsRef.current[chartId] = chart;
  };

  useLayoutEffect(() => {
    if (!isLoading && eventSummary?.length) {
      eventSummary.forEach((summary) => {
        if (document.getElementById(summary.event_group_code)) {
          eventSummaryChart(summary);
        }
      });
    }

    return () => {
      Object.values(chartsRef.current).forEach((chart) => chart.dispose());
      chartsRef.current = {};
    };
  }, [isLoading, isGlobalLoading, eventSummary]);

  const ReportingCards = ({ eventSummaryGroup }) => {
    const getEventDescription = (groupId) => {
      switch (groupId) {
        case "group-1":
          return "Number of times your ad has been viewed by visitors.";
        case "group-2":
          return "Number of times people clicked on your ad, or the content in it.";
        case "group-3":
          return "Digital contacts made, including emails and web enquiry forms.";
        case "group-4":
          return "Phone calls made to the tracked number in your listings.";
        default:
          console.warn("unknown event group id");
          return null;
      }
    };
    return (
      <Box flex="1" display="flex" flexDirection="columns" flexWrap="wrap" mui>
        {eventSummaryGroup &&
          eventSummaryGroup.map((summary, index) => {
            const eventDescription = getEventDescription(
              summary.event_group_code
            );
            return (
              <ReportingCard
                index={index}
                key={summary.event_group_code}
                summary={summary}
                eventDescription={eventDescription}
                id={summary.event_group_code}
              />
            );
          })}
      </Box>
    );
  };

  const ReportingCard = ({ summary, eventDescription, index }) => {
    const theme = useTheme();
    const { card } = makeStyles({
      card: {
        "&:hover": {
          boxShadow: theme.shadows[4],
        },
      },
    })();
    return (
      <Box
        className={card}
        component={Paper}
        flex={{ sm: "1 100", md: "1 45%", lg: "1" }}
        m={1}
        p={1}
        onClick={
          () =>
            history.push(
              `/reporting?period=${dateRange}&activeGroupIndex=${index}&isBreakdownReport=false`
            )
          // eslint-disable-next-line react/jsx-curly-newline
        }
      >
        <h3 className={classes.cardsHeader}>{summary.event_group_name}</h3>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: { xs: "100%", sm: "48%", md: "23%" }, // responsive: full, half, quarter
              p: 1,
              boxSizing: "border-box",
              borderRadius: 1,
            }}
          >
            {/* circular avatar */}
            <Skeleton variant="circular" width={40} height={40} />

            {/* text column */}
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="40%" height={16} />
            </Box>
          </Box>
        ) : (
          <Box id={summary.event_group_code} />
        )}

        <p>{eventDescription}</p>
      </Box>
    );
  };

  const handleDateRange = (e) => {
    setDateRange(e.target.value);
    setSelectedDate(getDateRange(e.target.value, "event-summary"));
  };

  return (
    <MainContent>
      <Box display="flex" alignItems="flex-end">
        <Box flex="2">
          <h1>My Reporting</h1>
          <p>
            See how your products are performing. Click to see more of each
            segment. This data is accurate to the last 3 days.
          </p>
        </Box>
        <Box p={2}>
          <Select
            value={dateRange}
            variant="outlined"
            input={
              // eslint-disable-next-line react/jsx-wrap-multilines
              <OutlinedInput
                classes={{ root: classes.customInput }}
                style={{
                  height: 32,
                  padding: "4px 8px",
                  fontSize: "14px",
                  borderColor: "black", // base border color
                }}
              />
            }
            onChange={(e) => {
              handleDateRange(e);
            }}
          >
            <MenuItem aria-label="None" value={0.25}>
              Last 7 Days
            </MenuItem>
            <MenuItem aria-label="None" value={1}>
              Last 30 Days
            </MenuItem>
            <MenuItem aria-label="None" value={6}>
              Last 6 Months
            </MenuItem>
            <MenuItem aria-label="None" value={12}>
              Last 12 Months
            </MenuItem>
          </Select>
        </Box>
      </Box>
      <ReportingCards eventSummaryGroup={eventSummary} />
    </MainContent>
  );
};

export default withRouter(MyAccountDashboard);
